import * as Sentry from '@sentry/react';

import { subscribeProfile } from '@core/profile/store.js';
import type { Tag } from '@core/tags/types.js';

import { sentryActorSend } from './sentry-machine.js';

let profileSubscribed = false;

function startProfileSubscriptionOnce(): void {
  if (profileSubscribed) return;
  profileSubscribed = true;
  const unsub = subscribeProfile((p) => {
    sentryActorSend(
      p.userId ? { type: 'PROFILE_SET', userId: p.userId } : { type: 'PROFILE_CLEAR' },
    );
  });
  sentryActorSend({ type: 'START_PROFILE_SUBSCRIPTION', unsub });
}

/**
 * Sentry tag — registers the user-context machine + drops a breadcrumb on
 * every analytics event. Necessary-tier per the cookiewet's strictly-
 * necessary carve-out (Sentry only collects errors + a small in-memory
 * breadcrumb buffer that ships with sent errors).
 *
 * - `onSet`: idempotent profile subscription. The first call after page load
 *   (which c15t guarantees by firing on hydration with the necessary
 *   category already granted) starts forwarding `PROFILE_SET` / `PROFILE_CLEAR`
 *   into the Sentry machine. Subsequent calls are no-ops.
 * - `onTrack`: `Sentry.addBreadcrumb` fires unconditionally — Sentry is always
 *   ready; no queue, no machine event needed for this path.
 *
 * Sentry's SDK init happens at module top in [`main.tsx`](../../spa/main.tsx)
 * so render-time crashes during boot are captured.
 */
export const sentryTag: Tag = {
  onSet: () => startProfileSubscriptionOnce(),
  onTrack: (event, data) => {
    Sentry.addBreadcrumb({
      category: 'analytics',
      message: event,
      level: 'info',
      data,
    });
  },
};

/** Test-only: reset the subscription guard between tests. */
export function __resetSentryTagForTests(): void {
  profileSubscribed = false;
}
